/**
 * Page title per route (ENG-APP-UX-TUNEUP Track A, floor row 1).
 *
 * Source: docs/product/experience-and-ui/43-cross-cutting-ui-patterns.md
 * § Accessibility — page title. `usePageTitle` writes `<Page> · ASOLOOP` to
 * `document.title` whenever the route or the UI language changes; it is
 * mounted ONCE, inside the router, by `components/layout/PageTitleSync.tsx`
 * (every route — public, admin and shell — carries its title, so the mount
 * sits above `Layout`). `Layout` reads the same title through
 * `usePageTitleText` for the route-change announcement.
 */
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { composeDocumentTitle, resolvePageTitleKey } from "@/lib/routeMeta";

/** The translated page title for `pathname` (no brand suffix). */
export function usePageTitleText(pathname: string): string {
  const { t } = useTranslation();
  const key = resolvePageTitleKey(pathname);
  // The rule table resolves a key at runtime; the strict key type cannot
  // express that, and `verify_sidebar_accordion_runtime` pins every key in the
  // table against `en.json` (the catalog-parity verify carries it to the other
  // locales), so the cast hides no typo.
  return t(key as never) as string;
}

/**
 * Keeps `document.title` at `<Page> · ASOLOOP` for the current route and
 * language. Returns the page title.
 */
export function usePageTitle(): string {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const title = usePageTitleText(pathname);
  useEffect(() => {
    document.title = composeDocumentTitle(title);
  }, [title, i18n.language]);
  return title;
}
